import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { IPromotionalCode } from 'src/app/models';
import { PromotionalCodeDiscountType, UserRole } from 'src/app/models/enums';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { images } from 'src/images';

@Component({
    selector: 'app-promotional-codes-tab',
    templateUrl: './promotional-codes-tab.component.html',
    styleUrl: './promotional-codes-tab.component.scss',
    standalone: false
})
export class PromotionalCodesTabComponent implements OnChanges {
  @Input() promotionalCodes: IPromotionalCode[] = [];
  @Input() selectedPromotionalCode: IPromotionalCode | undefined;
  @Input() clientId: number;
  @Input() userRole: UserRole;
  @Input() hideFooter = false;
  @Output() action = new EventEmitter<any>();
  @Output() deleteEvent = new EventEmitter<IPromotionalCode>();

  public images = images;
  public UserRole = UserRole;
  public PromotionalCodeDiscountType = PromotionalCodeDiscountType;

  public isCreating = false;
  public isEditing = false;

  public editingCode: IPromotionalCode = this.getEmpty();

  constructor(public literalService: LiteralService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['selectedPromotionalCode'] || changes['promotionalCodes']) && this.selectedPromotionalCode) {
      const code = this.promotionalCodes.find(c => c.id === this.selectedPromotionalCode!.id);
      this.editingCode = { ...(code ?? this.selectedPromotionalCode) };
    }
  }

  getEmpty(): IPromotionalCode {
    return {
      id: undefined,
      code: '',
      client: undefined,
      discountType: PromotionalCodeDiscountType.ABSOLUTE,
      discountValue: 0,
      validFrom: null,
      validUntil: null,
      maxUsages: null,
      currentUsages: 0,
      active: true
    };
  }

  getDiscountTypes(): any[] {
    return [
      { id: PromotionalCodeDiscountType.ABSOLUTE, value: PromotionalCodeDiscountType.ABSOLUTE, label: this.literalService.get('promotionalCodeDiscountType.ABSOLUTE', true) },
      { id: PromotionalCodeDiscountType.RELATIVE, value: PromotionalCodeDiscountType.RELATIVE, label: this.literalService.get('promotionalCodeDiscountType.RELATIVE', true) }
    ];
  }

  getCodesForSelect(): any[] {
    const suffix = ' (' + this.literalService.get('services.routes.deactivated', true) + ')';
    return this.promotionalCodes.map(c => ({
      id: c.id,
      value: c.id,
      label: c.active === false ? `${c.code}${suffix}` : c.code
    }));
  }

  isCurrentDeactivated(): boolean {
    return !!this.editingCode.id && this.editingCode.active === false;
  }

  startCreating() {
    this.isCreating = true;
    this.isEditing = false;
    this.editingCode = this.getEmpty();
  }

  startEditing() {
    if (this.editingCode.id) {
      this.isEditing = true;
      this.isCreating = false;
    }
  }

  cancelEdit() {
    this.isCreating = false;
    this.isEditing = false;
    if (this.editingCode.id) {
      const original = this.promotionalCodes.find(c => c.id === this.editingCode.id);
      this.editingCode = { ...(original ?? this.editingCode) };
    } else {
      this.editingCode = this.getEmpty();
    }
  }

  selectCode(event: any) {
    const selectedId = event.options ? event.options[0]?.id : event.target?.value;
    const code = this.promotionalCodes.find(c => c.id === selectedId);
    if (code) {
      this.editingCode = { ...code };
      this.isCreating = false;
      this.isEditing = false;
    }
  }

  changeDiscountType(event: any) {
    const typeValue = event.options ? event.options[0]?.value : event.target?.value;
    this.editingCode.discountType = typeValue as PromotionalCodeDiscountType;
  }

  updateCode(event: any) {
    this.editingCode.code = event.target?.value ?? event;
  }

  updateDiscountValue(event: any) {
    const value = event.target?.value ?? event;
    this.editingCode.discountValue = value ? parseFloat(value) : 0;
  }

  updateMaxUsages(event: any) {
    const value = event.target?.value ?? event;
    this.editingCode.maxUsages = value ? parseInt(value, 10) : null;
  }

  updateValidFrom(event: any) {
    const value = event.target?.value ?? event;
    this.editingCode.validFrom = this.localInputToUtc(value);
  }

  updateValidUntil(event: any) {
    const value = event.target?.value ?? event;
    this.editingCode.validUntil = this.localInputToUtc(value);
  }

  onDelete() {
    if (this.editingCode.id) {
      this.deleteEvent.emit(this.editingCode);
    }
  }

  onSubmit() {
    this.action.emit({
      promotionalCode: { ...this.editingCode, clientId: this.clientId },
      isCreating: this.isCreating,
      isEditing: this.isEditing
    });
  }

  async onEditSuccess(updated: IPromotionalCode): Promise<void> {
    this.isEditing = false;
    this.isCreating = false;
    this.editingCode = { ...updated };
  }

  async onCreateSuccess(created: IPromotionalCode): Promise<void> {
    this.isCreating = false;
    this.isEditing = false;
    this.editingCode = { ...created };
  }

  onDeleteSuccess(): void {
    this.isCreating = false;
    this.isEditing = false;
    this.editingCode = this.getEmpty();
  }

  resetState(): void {
    this.isCreating = false;
    this.isEditing = false;
    this.editingCode = this.getEmpty();
  }

  isFormValid(): boolean {
    if (!this.editingCode.code || !this.editingCode.discountType) return false;
    if (this.editingCode.discountValue === undefined || this.editingCode.discountValue === null) return false;
    if (this.editingCode.discountType === PromotionalCodeDiscountType.RELATIVE) {
      if (this.editingCode.discountValue <= 0 || this.editingCode.discountValue > 100) return false;
    } else if (this.editingCode.discountValue <= 0) {
      return false;
    }
    return true;
  }

  toDatetimeLocal(value: string | null | undefined): string {
    if (!value) return '';
    // BE stores/returns LocalDateTime as UTC wall clock without a tz marker
    // (e.g. "2025-12-25T10:30:00"). Treat it as UTC and render in the user's
    // local timezone for the datetime-local input.
    const iso = /[Zz]$|[+-]\d\d:?\d\d$/.test(value) ? value : `${value}Z`;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  private localInputToUtc(value: string | null | undefined): string | null {
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d.getTime())) return null;
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:00`;
  }
}
